import { inject, Injectable } from '@angular/core';
import { CrudService, type UpdateOptions } from '@context/frontend/api-client';
import { StorageService } from '@context/frontend/storage';
import { AuthService } from '@context/frontend/user';
import {
	CompanionCodeStatusResponse,
	MAX_COMPANION_CODE_LENGTH,
	Organization,
} from '@context/shared/types/organization';
import { getDoc, getDocs, query, where } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OrganizationService extends CrudService<Organization> {
	private readonly storageService = inject(StorageService);
	private readonly authService = inject(AuthService);
	static readonly CollectionId = 'organizations';

	constructor() {
		super(OrganizationService.CollectionId, {
			useOrganization: false,
			version: '1',
		});
	}

	/**
	 * Uploads a logo image for an organization, and returns the storage path to apply
	 * to the organization. This will also detect if the old logo needs to be removed and remove it.
	 *
	 * @param organizationData The current organization data
	 * @param newOrganizationData The new organization data being applied
	 * @param file The file (or lack thereof) to update the organization with
	 * @param organizationId The organization ID for the storage path
	 */
	async updateLogo(
		organizationData: Organization | null,
		newOrganizationData: Partial<Organization>,
		file: File | null,
	): Promise<{ url: string | null; updated: boolean }> {
		const previousUrl = organizationData?.logoUrl;

		// if the new organization doesn't have a logo, but the old one did, lets delete
		let deleteLogo = !newOrganizationData.logoUrl && !!previousUrl;

		const hasLogoChanged = file || deleteLogo;
		if (!hasLogoChanged) return { url: null, updated: false };

		let url: string | null = null;
		if (file) {
			url = await this.storageService
				.uploadFile(file, { entityType: 'details' })
				.catch((error) => {
					console.error(error);
					throw new Error(
						'OrganizationService.updateLogo: There was an issue uploading the file',
					);
				})
				.then((result) =>
					this.storageService.getDownloadUrl(result.ref),
				);
			deleteLogo = true;
		}

		// lets wait to delete the thumbnail until we determine the new thumbnail is successful
		if (previousUrl && deleteLogo) {
			// no reason to wait for it
			this.storageService.deleteFromURL(previousUrl).catch((error) => {
				console.error(
					'OrganizationService.updateLogo: There was an issue removing the old image from:',
					previousUrl,
					error,
				);
			});
		}

		return { url, updated: true };
	}

	/**
	 * Overrides the base update method to automatically refresh the authService.organization
	 * if the updated organization matches the current organization.
	 */
	override async update(
		payload: Partial<Organization & { id: string }>,
		options: UpdateOptions,
	): Promise<Partial<Organization>> {
		const result = await super.update(payload, options);

		const organization = this.authService.organization();
		const organizationRef = this.authService.organizationRef();
		if (!organization || !organizationRef)
			throw new Error('Organization not found');

		// If the updated organization matches the current organization, refresh it
		if (payload.id && organization.id === payload.id && organizationRef) {
			const updatedSnapshot = await getDoc(organizationRef);
			if (!updatedSnapshot.exists())
				throw new Error('Organization not found');

			this.authService.organization.set(
				updatedSnapshot.data() as Organization,
			);
		}

		return result;
	}

	/**
	 * Revokes (removes) the companion code from the organization via backend endpoint.
	 * Uses orgId from authenticated user's token.
	 *
	 * @param organizationId The organization ID (for refreshing local state)
	 */
	async revokeCompanionCode() {
		await firstValueFrom(
			this.http.get<{ success: boolean }>(
				`${this.api}/v1/organizations/companion-code/admin/revoke`,
			),
		);

		const orgRef = this.authService.organizationRef()!;
		const updatedSnapshot = await getDoc(orgRef);
		if (updatedSnapshot.exists()) {
			this.authService.organization.set(
				updatedSnapshot.data() as Organization,
			);
		}
	}

	/**
	 * Rotates (generates a new) companion code for an organization.
	 * This is the preferred method that uses encryption.
	 * Requires admin/owner role.
	 *
	 * @returns The new generated companion code (in lowercase) and updatedAt timestamp
	 */
	async adminRotateCompanionCode(): Promise<{
		code: string;
		updatedAt: Date;
	}> {
		const response = await firstValueFrom(
			this.http.get<{ code: string; updatedAt: string }>(
				`${this.api}/v1/organizations/companion-code/admin/rotate`,
			),
		);

		// Refresh organization data
		const orgRef = this.authService.organizationRef()!;
		const updatedSnapshot = await getDoc(orgRef);
		if (updatedSnapshot.exists()) {
			this.authService.organization.set(
				updatedSnapshot.data() as Organization,
			);
		}

		return {
			code: response.code,
			updatedAt: new Date(response.updatedAt),
		};
	}

	/**
	 * Retrieves the current companion code for an organization (decrypted).
	 * Requires admin/owner role.
	 *
	 * @returns The current companion code (in lowercase)
	 */
	readonly adminGetCompanionCode = () =>
		firstValueFrom(
			this.http.get<{ code: string }>(
				`${this.api}/v1/organizations/companion-code/admin`,
			),
		).then((response) => response.code);

	/**
	 * Fetches companion code status from private/security (via API).
	 * Used by organization details so the UI reflects private/security, not the org doc.
	 *
	 * @returns Status and optional timestamps (ISO strings)
	 */
	getCompanionCodeStatus(): Promise<CompanionCodeStatusResponse> {
		return firstValueFrom(
			this.http.get<CompanionCodeStatusResponse>(
				`${this.api}/v1/organizations/companion-code/admin/status`,
			),
		);
	}

	/**
	 * Finds an organization by its companion code.
	 * Companion codes are stored in lowercase, so the input is converted to lowercase.
	 *
	 * @param code The companion code to search for (case-insensitive)
	 * @returns The organization if found, null otherwise
	 */
	async findByCompanionCode(code: string): Promise<Organization | null> {
		const normalizedCode = code.toLowerCase().trim();
		if (!normalizedCode) return null;
		if (normalizedCode.length !== MAX_COMPANION_CODE_LENGTH) return null;

		const orgQuery = query(
			this.collection,
			where('companionCode', '==', normalizedCode),
		);

		const snapshot = await getDocs(orgQuery);
		if (snapshot.empty) return null;

		const doc = snapshot.docs[0];
		return { ...doc.data(), id: doc.id, ref: doc.ref } as Organization;
	}

	/**
	 * Creates a new organization and invites an owner.
	 * Requires super admin authentication.
	 *
	 * @param payload Organization creation data including owner invite details
	 * @returns The created organization ID
	 */
	createOrganizationWithOwner(payload: {
		name: string;
		logoUrl?: string | null;
		ownerEmail: string;
		ownerGivenName?: string;
		ownerFamilyName?: string;
		ownerMessage?: string;
	}): Promise<{ id: string }> {
		return firstValueFrom(
			this.http.post<{ id: string }>(
				`${this.api}/v1/organizations/create`,
				payload,
			),
		);
	}
}
